import type { Video } from '@ria-client/supergraph/structuredContent/hydration/WpVideo/types';

interface VideoObjectNode {
  '@type': 'VideoObject';
  description: string;
  duration?: string;
  embedUrl: string;
  name?: string;
  thumbnailUrl?: string;
  uploadDate: string;
}

type VideoSchema = VideoObjectNode & { '@context': 'https://schema.org' };

/**
 * VideoObject as nerdwallet.com emits it (seo-pages VideoWrapper.astro): a YouTube embed URL and a
 * single thumbnail. Shared by the standalone video block and the roundup Article's `video` list.
 */
export function getVideoObject(video: Video): VideoObjectNode {
  const { contentDetails, id, snippet } = video.youtubeVideo;
  const { description, publishedAt, thumbnails, title } = snippet;

  return {
    '@type': 'VideoObject',
    description,
    ...(contentDetails?.duration && { duration: contentDetails.duration }),
    embedUrl: `https://www.youtube.com/embed/${id}`,
    name: title,
    thumbnailUrl: [
      thumbnails?.standard,
      thumbnails?.default,
      thumbnails?.high,
      thumbnails?.medium,
      thumbnails?.maxres,
    ].find(thumbnail => thumbnail?.url)?.url,
    uploadDate: publishedAt,
  };
}

function LinkedDataJson({ id, schema }: { id: string; schema: VideoSchema }) {
  const __html = JSON.stringify(schema)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');

  return <script dangerouslySetInnerHTML={{ __html }} id={id} type="application/ld+json" />;
}

export function VideoLinkedData({ id, video }: { id: string; video: Video }) {
  if (!video?.youtubeVideo) return null;

  return (
    <LinkedDataJson
      id={id}
      schema={{ '@context': 'https://schema.org', ...getVideoObject(video) }}
    />
  );
}
